<script setup lang="ts">
import type { Sentiment } from '~kui/types/feedback';

import { Button } from '~ds/components/ui/button';
import { Text } from '~ds/components/ui/typography';
import { animate, motion, motionValue } from 'motion-v';
import { useI18n } from 'vue-i18n';

export interface Props {
  title: string;
  mentorName?: string;
  mentorPictureUrl?: string | '';
}

withDefaults(defineProps<Props>(), {
  mentorName: 'Blinky',
  mentorPictureUrl: '',
});

const emit = defineEmits<{
  submit: [sentiment: Sentiment];
  skip: [];
}>();

const { t } = useI18n();

const SENTIMENTS: { value: Sentiment; emoji: string; labelKey: string }[] = [
  { value: 'NEGATIVE', emoji: '🙁', labelKey: 'unified.activity.sentiment.negative' },
  { value: 'NEUTRAL', emoji: '😐', labelKey: 'unified.activity.sentiment.neutral' },
  { value: 'POSITIVE', emoji: '😍', labelKey: 'unified.activity.sentiment.positive' },
];

const THUMB_SIZE = 48;
const GUTTER = 8;

const trackRef = ref<HTMLElement | null>(null);
const sliderIndex = ref(1);
const isSubmitting = ref(false);
const thumbX = motionValue(0);

const activeSentiment = computed(() => SENTIMENTS[sliderIndex.value]);

const getSnapX = (index: number): number => {
  if (!trackRef.value) {
    return 0;
  }
  const trackWidth = trackRef.value.clientWidth;
  const usable = trackWidth - THUMB_SIZE - GUTTER * 2;
  return GUTTER + (index / 2) * usable;
};

const snapTo = (index: number) => {
  sliderIndex.value = index;
  animate(thumbX, getSnapX(index), {
    type: 'spring',
    stiffness: 250,
    damping: 12,
  });
};

const handleSelect = (index: number) => {
  if (isSubmitting.value) {
    return;
  }
  snapTo(index);
};

const onDragEnd = () => {
  if (isSubmitting.value) {
    return;
  }
  const trackWidth = trackRef.value?.clientWidth ?? 1;
  const currentX = thumbX.get();
  const ratio = currentX / (trackWidth - THUMB_SIZE);
  const nearest = Math.round(Math.max(0, Math.min(2, ratio * 2)));
  snapTo(nearest);
};

const handleSubmit = () => {
  if (isSubmitting.value) {
    return;
  }
  isSubmitting.value = true;
  emit('submit', activeSentiment.value?.value ?? 'NEUTRAL' as Sentiment);
};

const emojiButtonClasses = cva('flex flex-col items-center gap-1 transition-all duration-200 cursor-pointer', {
  variants: {
    active: {
      true: 'opacity-100',
      false: 'opacity-50',
    },
  },
});

const emojiLabelClasses = cva('text-xs mt-2 sm:text-xs font-medium transition-all duration-200', {
  variants: {
    active: {
      true: 'text-foreground',
      false: 'text-muted-foreground',
    },
  },
});

onMounted(() => {
  nextTick(() => snapTo(sliderIndex.value));
});
</script>

<template>
  <div class="flex h-full flex-col bg-background">
    <div class="flex flex-1 flex-col items-center justify-center gap-0 overflow-y-auto px-4 pt-4 md:pt-8 sm:px-6">
      <div class="flex items-center justify-center gap-0">
        <div class="z-30 size-10 shrink-0 overflow-hidden rounded-full border-2 border-secondary bg-white/50 backdrop-blur-sm">
          <NuxtImg
            v-if="mentorPictureUrl"
            :src="mentorPictureUrl"
            :alt="mentorName"
            class="size-full object-cover"
          />
          <KUIUnifiedBlinky
            v-else
            class="size-full object-cover bg-secondary"
          />
        </div>
        <div class="-ml-4 z-20 whitespace-nowrap rounded-2xl bg-secondary py-2 px-4 text-xs font-medium uppercase text-secondary-foreground">
          {{ mentorName }}
        </div>
      </div>

      <div class="-mt-4 mb-2 md:mb-4">
        <KUICoreCard
          padding="md"
          rounding="full"
          elevation="none"
        >
          <Text
            size="xl"
            class="text-center font-semibold"
          >
            {{ title }}
          </Text>
        </KUICoreCard>
      </div>

      <div v-if="activeSentiment" class="mt-3 w-full max-w-sm sm:max-w-md md:mt-6">
        <KUICoreCard
          padding="md"
          rounding="3xl"
          elevation="none"
        >
          <div class="px-4">
            <div class="flex items-center justify-center py-4 md:py-8">
              <div class="flex size-28 md:size-32 items-center justify-center rounded-4xl bg-secondary transition-all duration-300">
                <span :key="activeSentiment.value" class="text-5xl md:text-6xl select-none transition-all duration-300">
                  {{ activeSentiment.emoji }}
                </span>
              </div>
            </div>

            <div
              ref="trackRef"
              class="relative my-4 md:my-6 mx-2 h-12 flex items-center"
              role="group"
              :aria-label="t('unified.activity.sentiment.submit')"
            >
              <div class="absolute inset-x-0 h-2.5 rounded-full bg-muted" />

              <button
                v-for="(item, index) in SENTIMENTS"
                :key="`tap-${item.value}`"
                class="absolute top-0 z-10 h-full w-1/3 cursor-pointer focus:outline-none"
                :style="{ left: `${index * 33.333}%` }"
                :aria-label="t(item.labelKey)"
                :aria-pressed="sliderIndex === index"
                :disabled="isSubmitting"
                :data-testid="`sentiment-slider-option-${index}`"
                @click="handleSelect(index)"
              />

              <motion.div
                drag="x"
                :drag-constraints="trackRef ? { left: GUTTER, right: trackRef.clientWidth - THUMB_SIZE - GUTTER } : false"
                :drag-elastic="0.1"
                :drag-momentum="false"
                :on-drag-end="onDragEnd"
                :style="{ x: thumbX }"
                class="absolute z-20 size-12 cursor-grab rounded-full border-4 border-white bg-secondary shadow-md active:cursor-grabbing focus:outline-none"
              />
            </div>

            <div class="mt-2 py-3 md:mt-3 md:py-4 flex justify-between px-4">
              <button
                v-for="(item, index) in SENTIMENTS"
                :key="item.value"
                :class="emojiButtonClasses({ active: sliderIndex === index })"
                :disabled="isSubmitting"
                :data-testid="`sentiment-emoji-option-${index}`"
                @click="handleSelect(index)"
              >
                <span class="text-xl select-none">{{ item.emoji }}</span>
                <span :class="emojiLabelClasses({ active: sliderIndex === index })">
                  {{ t(item.labelKey) }}
                </span>
              </button>
            </div>
          </div>
        </KUICoreCard>
      </div>
    </div>

    <div class="max-w-xl mx-auto w-full shrink-0 px-4 pb-3 md:pb-6 pt-2 sm:px-6">
      <Button
        size="lg"
        variant="secondary"
        :disabled="isSubmitting"
        data-testid="sentiment-submit-button"
        type="button"
        class="w-full"
        @click="handleSubmit"
      >
        {{ t('unified.activity.sentiment.submit') }}
      </Button>
    </div>
  </div>
</template>
